import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import languageContext from "../context/language/languageContext";
import Navbar from "../components/Navbar";
import { useLocation } from "react-router-dom";
import { CalendarDays, Clock4, UserCheck } from "lucide-react";

const ViewAppointments = () => {
  const { setAlert, setMessage, setSessionTimeout } = useContext(languageContext);
  const [appointments, setAppointments] = useState([]);
  const location = useLocation();
  const patient_name = location.state?.patient_name || "Child";

  const fetchAppointments = async () => {
    const patient_id = location.state?.patient_id;
    if (!patient_id) return;

    try {
      const response = await axios.post(
        "https://www.careplus.gopillz.com/nurse_appointment_details",
        JSON.stringify({ id: patient_id }),
        {
          headers: {
            Accept: "*/*",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        setAppointments(response.data.data || []);
      }
    } catch (error) {
      if (error.response?.data?.detail === "Token has expired") {
        setSessionTimeout(true);
      } else {
        setAlert(true);
        setMessage("Failed to fetch appointments. Try again later.");
        console.error(error);
      }
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, []);

  const formatStatus = (status) => {
    if (status === "1") return { text: "🟡 Proposed", className: "bg-yellow-200 text-yellow-800" };
    if (status === "2") return { text: "🟢 Confirmed", className: "bg-green-200 text-green-800" };
    return { text: "⚪ Unknown", className: "bg-gray-200 text-gray-700" };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-emerald-50 to-sky-100">
      <Navbar />

      <div className="max-w-6xl mx-auto px-4 sm:px-8 pt-28 pb-12">
        <div className="bg-white/80 backdrop-blur-md shadow-2xl rounded-3xl p-8 sm:p-12 border border-emerald-200">
          <h2 className="text-4xl font-bold text-emerald-800 text-center mb-6 tracking-tight">
            👨‍👩‍👧 Parent Dashboard – Child Appointments
          </h2>
          <p className="text-center text-lg text-gray-700 mb-10">
            Viewing appointments for{" "}
            <span className="text-emerald-700 font-semibold">{patient_name}</span>
          </p>

          {appointments.length === 0 ? (
            <div className="text-center text-gray-500 italic py-16">
              No appointments scheduled yet.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {appointments.map((appointment) => {
                const { text, className } = formatStatus(appointment.status);
                return (
                  <div
                    key={appointment.id}
                    className="bg-white rounded-2xl p-6 border border-emerald-100 shadow-md hover:shadow-xl transition-all duration-300"
                  >
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-emerald-900 font-semibold text-lg flex items-center">
                        <UserCheck className="w-5 h-5 mr-2 text-emerald-600" />
                        Appointment #{appointment.id}
                      </span>
                      <span className={`text-xs px-3 py-1 rounded-full font-medium ${className}`}>
                        {text}
                      </span>
                    </div>

                    <div className="text-gray-700 space-y-3 text-sm">
                      <p className="flex items-center">
                        <CalendarDays className="w-5 h-5 mr-2 text-emerald-500" />
                        <span className="font-medium">Date:</span> {appointment.date}
                      </p>
                      <p className="flex items-center">
                        <Clock4 className="w-5 h-5 mr-2 text-emerald-500" />
                        <span className="font-medium">Time:</span> {appointment.time}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewAppointments;
